/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 스크롤 클래스를 정의한다.
 *
 * @file /src/utils/Scroller.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 9. 25.
 */
import * as parent from '@base/Base';
import { Dom } from '@utils/Dom';
import { Html } from '@utils/Html';
import { Dragger } from '@utils/Dragger';
import { AnimationFrames } from '@utils/AnimationFrames';
import { Exceptions } from '@utils/Exceptions';
import { Format } from '@utils/Format';

export interface Listeners<T> extends parent.Listeners<T> {
    /**
     * 스크롤되는 영역의 크기가 변경되었을 때
     */
    resize?: (me: Scroller) => void;

    /**
     * 스크롤이 활성화되었을 때
     */
    active?: (me: Scroller, direction: string) => void;

    /**
     * 스크롤이 비활성화되었을 때
     */
    deactive?: (me: Scroller, direction: string) => void;

    /**
     * 스크롤위치가 변경되었을 때
     */
    update?: (me: Scroller, position: { x: number; y: number }) => void;
}

export interface Properties<T> extends parent.Properties<T> {
    /**
     * 스크롤여부
     */
    scrollable: 'x' | 'y' | boolean;

    /**
     * 마우스 오버시 스크롤 활성화여부
     */
    isHovered?: boolean;

    /**
     * 스크롤 대상 DOM
     */
    $target: Dom;

    /**
     * 스크롤할 대상의 컴포넌트 DOM
     */
    $component: Dom;

    /**
     * 이벤트리스너
     */
    listeners?: Listeners<T>;
}

export class Scroller extends parent.Base {
    /**
     * 전체 스크롤 객체 및 활성화 여부
     */
    static scrollers: Map<HTMLElement, Scroller> = new Map();

    /**
     * 스크롤바 업데이트를 위한 AFId
     */
    private static AFId: number = null;

    /**
     * 스크롤 여부
     */
    scrollable: { x: boolean; y: boolean } = { x: false, y: false };

    /**
     * 마우스 오버시 스크롤 활성화여부
     */
    isHovered: boolean;

    /**
     * 마지막으로 설정된 위치
     */
    latestPosition: { x: number; y: number } = { x: 0, y: 0 };

    /**
     * 마지막으로 움직인 시각
     */
    latestScrolledAt: { x: number; y: number } = { x: 0, y: 0 };

    /**
     * 스크롤 영역이 포함된 Component, Element 의 DOMRect
     */
    componentRect: DOMRect = null;

    /**
     * 스크롤 영역의 DOMRect
     */
    targetRect: DOMRect = null;

    /**
     * 스크롤 가능영역
     */
    scrollOffset: { x: number; y: number } = null;

    /**
     * 스크롤 대상 DOM
     */
    $target: Dom = null;

    /**
     * 스크롤할 대상의 컴포넌트 DOM
     */
    $component: Dom = null;

    /**
     * 스크롤 Dom
     */
    $scroller: { x: Dom; y: Dom } = { x: null, y: null };

    /**
     * 트랙 여백
     */
    $scrollerSpace: { x: Dom; y: Dom } = { x: null, y: null };

    /**
     * 트랙 Dom
     */
    $scrollerTrack: { x: Dom; y: Dom } = { x: null, y: null };

    /**
     * 스크롤바 Dom
     */
    $scrollerBar: { x: Dom; y: Dom } = { x: null, y: null };

    /**
     * 드래거
     */
    dragger: { x: Dragger; y: Dragger } = { x: null, y: null };

    /**
     * 자동스크롤을 위한 AFID
     */
    private scrollAFId: { x: string; y: string } = { x: null, y: null };

    /**
     * 자동스크롤 속도
     */
    private scrollSpeed: { x: number; y: number } = { x: 0, y: 0 };

    /**
     * updatePosition 디바운스 타이머
     */
    private updatePositionTimer: ReturnType<typeof setTimeout> | null = null;

    /**
     * update 이벤트 디바운스 타이머
     * (스크롤이 빈번하게 발생할 때마다 매번 이벤트를 발생시키지 않고,
     * 스크롤이 멈추고 일정시간이 지난 후 한번만 발생시키기 위함)
     */
    private updateEventTimer: ReturnType<typeof setTimeout> | null = null;

    /**
     * 스크롤러 개별 객체를 정의한다.
     *
     * @param properties - 초기설정
     */
    constructor(public properties: Properties<Scroller> = null) {
        super(properties);

        this.scrollable.x = properties.scrollable === true || properties.scrollable == 'x';
        this.scrollable.y = properties.scrollable === true || properties.scrollable == 'y';
        this.isHovered = properties.isHovered !== false;

        this.$target = this.properties.$target ?? null;
        this.$component = this.properties.$component ?? null;

        if (this.$target === null || this.$component === null) {
            return;
        }

        Scroller.scrollers.set(this.$target.getEl(), this);
        this.updatePosition();

        /**
         * 스크롤바를 이용하는 객체가 존재할 경우, 스크롤바 업데이트를 위한 AF 를 시작한다.
         */
        if (Scroller.AFId === null) {
            Scroller.AFId = requestAnimationFrame(Scroller.tick.bind(this));
        }
    }

    /**
     * 스크롤되는 모든 객체의 스크롤바를 업데이트한다.
     */
    static tick(): void {
        Scroller.scrollers.forEach((scroller, element) => {
            if (element.isConnected == true) {
                if (scroller.isScrolled('x') == true) {
                    scroller.active('x');
                    scroller.updateTrack('x');
                } else if (scroller.isDeactivatable('x') == true) {
                    scroller.deactive('x');
                }

                if (scroller.isScrolled('y') == true) {
                    scroller.active('y');
                    scroller.updateTrack('y');
                } else if (scroller.isDeactivatable('y') == true) {
                    scroller.deactive('y');
                }
            }
        });

        if (Scroller.scrollers.size > 0) {
            Scroller.AFId = requestAnimationFrame(Scroller.tick.bind(this));
        }
    }

    /**
     * 스크롤 이벤트 대상의 스크롤 클래스 객체를 가져온다.
     *
     * @param properties - 초기설정
     * @returns 스크롤러 객체
     */
    static get(properties: Properties<Scroller>): Scroller {
        if (Scroller.scrollers.has(properties.$target.getEl()) == true) {
            return Scroller.scrollers.get(properties.$target.getEl());
        } else {
            return new Scroller(properties);
        }
    }

    /**
     * 스크롤 DOM 을 가져온다.
     *
     * @param direction - 가져올 스크롤축
     * @returns 스크롤 DOM
     */
    $getScroller(direction: 'x' | 'y'): Dom {
        if (this.$scroller[direction] == null) {
            const targetRect = this.getTargetRect();
            const componentRect = this.getComponentRect();
            const width = componentRect.width - targetRect.width;
            const height = componentRect.height - targetRect.height;
            const left = targetRect.left - componentRect.left;
            const right = componentRect.right - targetRect.right;
            const top = targetRect.top - componentRect.top;
            const bottom = componentRect.bottom - targetRect.bottom;
            this.$scroller[direction] = Html.create('div', {
                'data-role': 'scroller',
                'data-direction': direction,
            });

            const $space = this.$getScrollerSpace(direction);
            const $track = this.$getScrollerTrack(direction);
            const $bar = this.$getScrollerBar(direction);

            if (direction == 'x') {
                this.$scroller[direction].setStyle('left', left + 'px');
                this.$scroller[direction].setStyle('bottom', bottom + 'px');
                this.$scroller[direction].setStyle('width', 'calc(100% - ' + width + 'px)');
            } else {
                this.$scroller[direction].setStyle('right', right + 'px');
                this.$scroller[direction].setStyle('top', top + 'px');
                this.$scroller[direction].setStyle('height', 'calc(100% - ' + height + 'px)');
            }

            if (this.isHovered == true) {
                $track.addClass('hover');
            }

            $track.append($bar);
            this.$scroller[direction].append($space);
            this.$scroller[direction].append($track);
        }

        return this.$scroller[direction];
    }

    /**
     * 트랙 여백 DOM 을 가져온다.
     *
     * @param direction - 가져올 스크롤축
     * @returns 트랙 DOM
     */
    $getScrollerSpace(direction: 'x' | 'y'): Dom {
        this.$scrollerSpace[direction] ??= Html.create('div', { 'data-role': 'space' });
        return this.$scrollerSpace[direction];
    }

    /**
     * 트랙 DOM 을 가져온다.
     *
     * @param direction - 가져올 스크롤축
     * @returns 트랙 DOM
     */
    $getScrollerTrack(direction: 'x' | 'y'): Dom {
        this.$scrollerTrack[direction] ??= Html.create('div', { 'data-role': 'track' });
        return this.$scrollerTrack[direction];
    }

    /**
     * 스크롤바 DOM 을 가져온다.
     *
     * @param direction - 가져올 스크롤축
     * @returns 스크롤바 DOM
     */
    $getScrollerBar(direction: 'x' | 'y'): Dom {
        this.$scrollerBar[direction] ??= Html.create('div', { 'data-role': 'bar' });
        return this.$scrollerBar[direction];
    }

    /**
     * 스크롤바 트랙이 위치할 기준 좌표를 설정한다.
     *
     * @param {('x'|'y')} direction - 설정할 스크롤축
     * @param {number} position - 좌표
     */
    setTrackPosition(direction: 'x' | 'y', position: number | string): void {
        const $space = this.$getScrollerSpace(direction);
        if (direction == 'x') {
            $space.setStyle('width', typeof position == 'number' ? position + 'px' : position);
        } else {
            $space.setStyle('height', typeof position == 'number' ? position + 'px' : position);
        }
    }

    /**
     * 스크롤되는 영역의 DOMRect를 가져온다.
     *
     * @param is_update - 업데이트여부
     * @returns 스크롤 영역의 DOMRect
     */
    getTargetRect(is_update: boolean = false): DOMRect {
        if (is_update == true || this.targetRect === null) {
            this.targetRect = this.$target?.getEl()?.getBoundingClientRect() ?? null;
        }

        return this.targetRect;
    }

    /**
     * 스크롤되는 영역의 부모의 DOMRect 를 가져온다.
     *
     * @param is_update - 업데이트여부
     * @returns 스크롤 영역의 DOMRect
     */
    getComponentRect(is_update: boolean = false): DOMRect {
        if (is_update == true || this.componentRect === null) {
            this.componentRect = this.$component?.getEl()?.getBoundingClientRect() ?? null;
        }

        return this.componentRect;
    }

    /**
     * 스크롤 되는 영역의 스크롤 길이를 가져온다.
     *
     * @param direction - 스크롤 길이를 가져올 스크롤롤축
     * @param is_update - 업데이트여부
     * @returns 스크롤 길이
     */
    /**
     * 스크롤 되는 영역의 스크롤 길이
     */
    targetScrollLength: { x: number; y: number } = null;
    getTargetScrollLength(direction: 'x' | 'y', is_update: boolean = false): number {
        if (is_update == true || this.targetScrollLength === null) {
            this.targetScrollLength = { x: this.$target.getScrollWidth(), y: this.$target.getScrollHeight() };
        }

        return this.targetScrollLength[direction];
    }

    /**
     * 스크롤 가능영역을 가져온다.
     *
     * @param direction - 스크롤 영역을 가져올 스크롤롤축
     * @param is_update - 업데이트여부
     * @returns 스크롤 가능 영역
     */
    getScrollOffset(direction: 'x' | 'y', is_update: boolean = false): number {
        if (is_update == true || this.scrollOffset === null) {
            this.scrollOffset = {
                x: this.getTargetScrollLength('x', is_update) - this.$target.getInnerWidth(),
                y: this.getTargetScrollLength('y', is_update) - this.$target.getInnerHeight(),
            };
        }

        return this.scrollOffset[direction];
    }

    /**
     * 현재 스크롤 위치를 가져온다.
     *
     * @param is_latest - 마지막 스크롤 위치를 가져올지 여부
     * @returns 현재 스크롤 위치
     */
    getScrollPosition(is_latest: boolean = false): { x: number; y: number } {
        if (is_latest == true) {
            return this.latestPosition;
        }

        const scroll = this.$target.getScroll();
        return { x: scroll.left, y: scroll.top };
    }

    /**
     * 현재 스크롤 위치를 설정한다.
     *
     * @param x - X축 위치
     * @param y - Y축 위치
     * @param is_active - 스크롤바를 활성화할지 여부
     * @param is_animate - 애니메이션 여부
     */
    setPosition(x: number, y: number, is_active: boolean = false, is_animate: boolean = false): void {
        const position = this.getScrollPosition();

        x ??= position.x;
        y ??= position.y;

        if (x == position.x && y == position.y) {
            return;
        }

        if (is_active == true) {
            if (x != position.x) {
                this.active('x');
            }

            if (y != position.y) {
                this.active('y');
            }
        }

        this.$target.setScroll(x, y, is_animate === true);
    }

    /**
     * 스크롤위치에 따른 트랙의 위치를 가져온다.
     *
     * @param direction - 위치를 가져올 스크롤축
     * @returns 트랙위치
     */
    getScrollToTrackPosition(direction: 'x' | 'y'): number {
        const $track = this.$getScrollerTrack(direction);
        const $bar = this.$getScrollerBar(direction);

        if (this.getScrollOffset(direction) == 0) {
            return 0;
        }

        let trackLength = 0;
        if (direction == 'x') {
            trackLength = $track.getWidth() - $bar.getOuterWidth();
        } else {
            trackLength = $track.getHeight() - $bar.getOuterHeight();
        }

        if (trackLength == 0) {
            return 0;
        }

        return Math.round((this.getScrollPosition()[direction] / this.getScrollOffset(direction)) * trackLength);
    }

    /**
     * 트랙위치에 따른 스크롤 위치를 가져온다.
     *
     * @param {'x'|'y'} direction - 위치를 가져올 스크롤축
     * @returns {number} position - 스크롤위치
     */
    getTrackToScrollPosition(direction: 'x' | 'y', position: number): number {
        const $track = this.$getScrollerTrack(direction);
        const $bar = this.$getScrollerBar(direction);

        let trackLength = 0;
        if (direction == 'x') {
            trackLength = $track.getWidth() - $bar.getOuterWidth();
        } else {
            trackLength = $track.getHeight() - $bar.getOuterHeight();
        }

        return Math.round((position / trackLength) * this.getScrollOffset(direction));
    }

    /**
     * 스크롤이 가능한지 확인한다.
     *
     * @param direction - 스크롤 가능여부를 확인할 스크롤축
     * @returns 스크롤 가능여부
     */
    isScrollable(direction: 'x' | 'y', is_prevented: boolean = false): boolean {
        return (
            this.scrollable[direction] &&
            this.getScrollOffset(direction) > 0 &&
            (is_prevented == false || this.$target.getAttr('data-scroll-' + direction) != 'stop')
        );
    }

    /**
     * 마지막 스크롤된 위치로 부터 스크롤이 이동했는지 확인한다.
     *
     * @param direction - 스크롤 가능여부를 확인할 스크롤축
     * @returns 스크롤 가능여부
     */
    isScrolled(direction: 'x' | 'y'): boolean {
        return (
            this.isScrollable(direction, true) == true &&
            this.latestPosition[direction] != this.getScrollPosition()[direction]
        );
    }

    /**
     * 스크롤바가 비활성화될 수 있는지 확인한다.
     *
     * @param direction - 비활성화가능여부를 확인할 스크롤축
     * @returns 스크롤 가능여부
     */
    isDeactivatable(direction: 'x' | 'y'): boolean {
        const $scroller = this.$getScroller(direction);
        return $scroller.hasClass('active') == true && this.latestScrolledAt[direction] < Date.now() - 2000;
    }

    /**
     * 스크롤바를 활성화한다.
     *
     * @param direction - 활성화할 스크롤바 축
     */
    async active(direction: 'x' | 'y'): Promise<void> {
        this.latestScrolledAt[direction] = Date.now();
        const $scroller = this.$getScroller(direction);

        $scroller.addClass('active');

        if (this.hasEvent('active') == true) {
            this.fireEvent('active', [this, direction]);
        }
    }

    /**
     * 스크롤바를 비활성화한다.
     *
     * @param direction - 비활성화할 스크롤바 축
     * @param delay - 비활성화할 딜레이시간(0 인 경우 즉시 비활성화)
     */
    deactive(direction: 'x' | 'y'): void {
        const $scroller = this.$getScroller(direction);

        if ($scroller.hasClass('drag') == true) {
            return;
        }

        $scroller.removeClass('active');
        this.fireEvent('deactive', [this, direction]);
    }

    /**
     * 스크롤을 차단할지 여부를 설정한다.
     *
     * @param direction - 차단할 스크롤바 축
     * @param is_prevent - 스크롤 차단여부
     */
    preventScroll(direction: 'x' | 'y', is_prevent: boolean): void {
        if (is_prevent == true) {
            if (this.isScrollable(direction) == true) {
                this.$target.setAttr('data-scroll-' + direction, 'stop');
            }
        } else {
            if (this.$target.getAttr('data-scroll-' + direction) == 'stop') {
                this.$target.setAttr('data-scroll-' + direction, 'true');
            }
        }
    }

    /**
     * 특정 방향으로 자동 스크롤을 시작하거나 속도를 변경한다.
     *
     * @param direction - 스크롤 방향 ('x' | 'y')
     * @param speed - 프레임당 이동 속도 (px/frame)
     */
    startScroll(direction: 'x' | 'y', speed: number): void {
        this.scrollSpeed[direction] = speed;
        if (this.scrollAFId[direction] != null) {
            if (AnimationFrames.isActive(this.scrollAFId[direction]) == false) {
                AnimationFrames.loop(this.scrollAFId[direction]);
            }
            return;
        }

        this.scrollAFId[direction] = AnimationFrames.request(
            (): boolean => {
                const position = this.getScrollPosition();
                const offset = this.getScrollOffset(direction);
                const speed = this.scrollSpeed[direction];
                const newPos = Math.max(0, Math.min(offset, position[direction] + speed));

                if (position[direction] == newPos || speed == 0) {
                    this.stopScroll(direction);
                    return false;
                }

                if (direction === 'x') {
                    this.setPosition(newPos, null);
                } else {
                    this.setPosition(null, newPos);
                }

                this.fireEvent('active', [this, direction]);

                return true;
            },
            'AutoScroll',
            this
        );
    }

    /**
     * 자동 스크롤을 중지한다.
     *
     * @param direction - 스크롤 방향 ('x' | 'y')
     */
    stopScroll(direction: 'x' | 'y'): void {
        const uuid = this.scrollAFId[direction];
        this.scrollAFId[direction] = null;

        if (uuid !== null && AnimationFrames.isActive(uuid) == true) {
            AnimationFrames.cancel(uuid);
        }

        this.scrollSpeed[direction] = 0;
    }

    /**
     * 스크롤바 트랙을 업데이트한다.
     *
     * @param direction - 업데이트할 스크롤축
     */
    updateTrack(direction: 'x' | 'y'): void {
        const $track = this.$getScrollerTrack(direction);
        const $bar = this.$getScrollerBar(direction);

        switch (direction) {
            case 'x':
                $bar.setStyle(
                    'width',
                    Math.ceil(($track.getWidth() / this.getTargetScrollLength(direction)) * 100) + '%'
                );
                $bar.setStyle('left', this.getScrollToTrackPosition(direction) + 'px');
                break;

            case 'y':
                $bar.setStyle(
                    'height',
                    Math.ceil(($track.getHeight() / this.getTargetScrollLength(direction)) * 100) + '%'
                );
                $bar.setStyle('top', this.getScrollToTrackPosition(direction) + 'px');
                break;
        }

        const current = this.getScrollPosition();
        if (Format.isEqual(this.latestPosition, current) == false) {
            this.latestPosition = current;

            if (this.updateEventTimer !== null) {
                clearTimeout(this.updateEventTimer);
            }

            this.updateEventTimer = setTimeout(() => {
                this.updateEventTimer = null;
                this.fireEvent('update', [this, this.getScrollPosition()]);
            }, 200);
        }
    }

    /**
     * 스크롤바 위치를 조절한다.
     */
    updatePosition(): void {
        if (this.updatePositionTimer !== null) {
            clearTimeout(this.updatePositionTimer);
        }

        this.updatePositionTimer = setTimeout(() => {
            this.updatePositionTimer = null;
            this.doUpdatePosition();
        }, 100);
    }

    /**
     * 스크롤바 위치를 실제로 조절한다.
     */
    private doUpdatePosition(): void {
        const targetRect = this.getTargetRect(true);
        const componentRect = this.getComponentRect(true);

        if (targetRect === null || componentRect === null) {
            return;
        }

        this.getScrollOffset('x', true);
        this.getScrollOffset('y', true);

        const $scrollerX = this.$getScroller('x');
        const $scrollerY = this.$getScroller('y');
        const width = componentRect.width - targetRect.width;
        const height = componentRect.height - targetRect.height;
        const left = targetRect.left - componentRect.left;
        const top = targetRect.top - componentRect.top;
        const bottom = componentRect.bottom - targetRect.bottom;

        if (this.scrollable.x == true) {
            $scrollerX.setStyle('left', left + 'px');
            $scrollerX.setStyle('bottom', bottom + 'px');
            $scrollerX.setStyle('width', 'calc(100% - ' + width + 'px)');
        }

        if (this.scrollable.y == true) {
            $scrollerY.setStyle('right', '0px');
            $scrollerY.setStyle('top', top + 'px');
            $scrollerY.setStyle('height', 'calc(100% - ' + height + 'px)');
        }

        if (this.isScrollable('x') == true) {
            if (this.isScrollable('x', true) == true) {
                this.updateTrack('x');
            }
            $scrollerX.setData('disabled', 'false');
        } else {
            $scrollerX.setData('disabled', 'true');
        }

        if (this.isScrollable('y') == true) {
            if (this.isScrollable('y', true) == true) {
                this.updateTrack('y');
            }
            $scrollerY.setData('disabled', 'false');
        } else {
            $scrollerY.setData('disabled', 'true');
        }

        this.fireEvent('resize', [this]);
    }

    /**
     * 스크롤바 이벤트를 처리한다.
     *
     * @param direction - 이벤트를 처리할 스크롤축
     */
    setTrackEvent(direction: 'x' | 'y'): void {
        const $bar = this.$getScrollerBar(direction);
        this.dragger[direction] ??= new Dragger($bar, {
            listeners: {
                start: ($bar: Dom) => {
                    const $scroller = $bar.getParents('div[data-role=scroller]');
                    const direction = $scroller.getData('direction');
                    if (direction == 'x') {
                        $bar.setData('start', parseInt($bar.getStyle('left').replace(/px$/, ''), 10), false);
                    } else {
                        $bar.setData('start', parseInt($bar.getStyle('top').replace(/px$/, ''), 10), false);
                    }

                    $scroller.addClass('drag');
                },
                drag: ($bar: Dom, dragger: Dragger) => {
                    const $scroller = $bar.getParents('div[data-role=scroller]');
                    const $track = Html.get('div[data-role=track]', $scroller);
                    const direction = $scroller.getData('direction');

                    if (direction == 'x') {
                        const left = Math.min(
                            $track.getWidth() - $bar.getWidth(),
                            Math.max(0, $bar.getData('start') + dragger.getLength().x)
                        );

                        this.setPosition(this.getTrackToScrollPosition(direction, left), null);
                    } else {
                        const top = Math.min(
                            $track.getHeight() - $bar.getHeight(),
                            Math.max(0, $bar.getData('start') + dragger.getLength().y)
                        );

                        this.setPosition(null, this.getTrackToScrollPosition(direction, top));
                    }
                },
                end: ($bar: Dom) => {
                    const $scroller = $bar.getParents('div[data-role=scroller]');
                    $scroller.removeClass('drag');
                },
            },
        });
    }

    /**
     * 마우스 오버시 스크롤 활성화여부를 변경한다.
     *
     * @param direction - 가져올 스크롤축
     * @param isHovered - 마우스 오버시 스크롤 활성화여부
     */
    setHovered(isHovered: boolean): void {
        this.isHovered = isHovered;

        const $x = this.$getScrollerTrack('x');
        const $y = this.$getScrollerTrack('y');
        if (this.isHovered == true) {
            $x.addClass('hover');
            $y.addClass('hover');
        } else {
            $x.removeClass('hover');
            $y.removeClass('hover');
        }
    }

    /**
     * 스크롤를 랜더링한다.
     */
    async render(): Promise<void> {
        if (this.scrollable.x == false && this.scrollable.y == false) {
            return;
        }

        if (this.scrollable.x == true) {
            this.$target.setAttr('data-scroll-x', 'true');
            this.$component.append(this.$getScroller('x'));
            this.updateTrack('x');
        }

        if (this.scrollable.y == true) {
            this.$target.setAttr('data-scroll-y', 'true');
            this.$component.append(this.$getScroller('y'));
            this.updateTrack('y');
        }

        this.setTrackEvent('x');
        this.setTrackEvent('y');
    }

    /**
     * 스크롤영역을 제거한다.
     */
    async remove(): Promise<void> {
        Scroller.scrollers.delete(this.$target.getEl());

        if (this.updatePositionTimer !== null) {
            clearTimeout(this.updatePositionTimer);
            this.updatePositionTimer = null;
        }

        if (this.updateEventTimer !== null) {
            clearTimeout(this.updateEventTimer);
            this.updateEventTimer = null;
        }

        this.stopScroll('x');
        this.stopScroll('y');

        this.$scroller.x?.remove();
        this.$scroller.y?.remove();
        this.$scroller = { x: null, y: null };

        this.$scrollerTrack.x?.remove();
        this.$scrollerTrack.y?.remove();
        this.$scrollerTrack = { x: null, y: null };

        this.$scrollerBar.x?.remove();
        this.$scrollerBar.y?.remove();
        this.$scrollerBar = { x: null, y: null };

        this.$scrollerSpace = { x: null, y: null };

        this.dragger.x?.remove();
        this.dragger.y?.remove();
        this.dragger = { x: null, y: null };

        this.$target = null;
        this.$component = null;

        super.destroy();
    }

    /**
     * 디버그 데이터를 콘솔에 출력한다.
     */
    static debug(): void {
        Exceptions.debug('[Scroller]', `Total Scrollables: ${Scroller.scrollers.size}`);

        const tableData: any[] = [];

        for (const scroller of Scroller.scrollers.values()) {
            tableData.push({
                Component: scroller.$component,
                Type: scroller.$component.getData('type'),
                Variant: scroller.$component.getData('variant'),
                Target: scroller.$target.getEl(),
                ActiveEvent: scroller.listeners?.active?.length ?? 0,
            });
        }

        Exceptions.table(tableData);
    }
}
